import { useMemo } from "react";
import Module from "layout/Module";
import useCharts from "hooks/UD/useCharts";
import useCompareCharts from "hooks/UD/useCompareCharts";
import useEditions from "hooks/UD/useEditions";

import UDResultsSidebar from "./components/UDResultsSidebar";
import UDResultsMain from "./components/UDResultsMain";
import StepsBox from "components/StepsBox";
import Step1 from "./steps/Step1";
import Step2 from "./steps/Step2";

function UDModuleContent({ params, errors, setErrors, onChange, onSubmit }) {
   const { d: editions } = useEditions();
   
   const isCompareMode = params?.compare_mode === "compare";
   
   const editionData = useMemo(() => {
      if (!params?.edition || !editions) return null;
      return editions.find(e => e.value === params.edition) || null;
   }, [params?.edition, editions]);
   
   const edition2Data = useMemo(() => {
      if (!isCompareMode || !params?.edition2 || !editions) return null;
      return editions.find(e => e.value === params.edition2) || null;
   }, [isCompareMode, params?.edition2, editions]);

   // Jeśli edycja jest już wybrana, zacznij od kroku 2 (wybór zmiennych)
   const initialStep = useMemo(() => {
      if (isCompareMode) {
         return (params?.edition && params?.edition2) ? 2 : 1;
      }
      return params?.edition ? 2 : 1;
   }, [params?.edition, params?.edition2, isCompareMode]);

   return (
      <>
         <h2> Wybierz zmienne </h2>

         <p> Wybierz kategorie danych, które chcesz uwzględnić w generowanym raporcie. Dla każdej z nich możesz zaznaczyć dowolną liczbę zmiennych lub pominąć wybór. </p>

         <StepsBox
            params={params}
            errors={errors}
            setErrors={setErrors}
            onChange={onChange}
            onSubmit={onSubmit}
            editionData={editionData}
            initialStep={initialStep}
            steps={[
               { 
                  title: "Krok 1: Edycja badania",
                  next_btn: "Przejdź do wyboru zmiennej",
                  validation_fields: [
                     {
                        name: "compare_mode",
                        message: "Proszę wybierz tryb pracy"
                     },
                     {
                        name: "edition",
                        message: "Proszę wybierz edycję badania",
                        fn: data => {
                           if (data.compare_mode === "compare") {
                              return !!data.edition && !!data.edition2;
                           }
                           return !!data.edition;
                        }
                     },
                     {
                        name: "edition2",
                        message: "Proszę wybierz drugą edycję badania",
                        fn: data => {
                           if (data.compare_mode === "compare") {
                              return !!data.edition2;
                           }
                           return true; // Nie wymagane w trybie single
                        }
                     }
                  ],
                  Content: Step1 
               },
               { 
                  title: "Krok 2: Kluczowe zmienne",
                  prev_btn: "Wróć do wyboru edycji",
                  next_btn: "Generuj wyniki",
                  validation_fields: [{
                     name: "key_variable_ids",
                     message: "Wybierz kluczowe zmienne",
                     fn: data => data.key_variable_ids && !!data.key_variable_ids?.length
                  }],
                  Content: Step2
               }
            ]}
         />
      </>
   );
}

// Wrapper hook, który wybiera odpowiedni hook na podstawie trybu porównania
// Zawsze wywołujemy oba hooki, ale tylko jeden będzie aktywny dzięki warunkom w useFetchData
function useUDChartsWrapper(params) {
   const isCompareMode = params?.compare_mode === "compare";
   
   // Zawsze wywołujemy oba hooki, ale tylko jeden będzie aktywny
   const compareResult = useCompareCharts(params);
   const singleResult = useCharts(params);
   
   // Zwracamy wynik odpowiedniego hooka
   return isCompareMode ? compareResult : singleResult;
}

export default function UDModule() {
   return (
         <Module
         results_props={{
            dataHook: useUDChartsWrapper,
            SidebarComponent: UDResultsSidebar,
            MainComponent: UDResultsMain
         }}

         Component={UDModuleContent}
      />
   )
}